import {
  TableBody,
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import React, { useState } from "react";
import FacilitySidebar from "./FacilitySidebar";
import CustodianSidebar from "./CustodianSidebar";
import useAuth from "../../hooks/context/useAuth";
import { appActions } from "../../settings/appConfig";
import { green, red } from '@mui/material/colors';
import EditOffIcon from '@mui/icons-material/EditOff';

interface AssociateCustodianTableRowProps {
  custodianRow: any;
  facilityIndex?: number;
}

export default function AssociateCustodianTableRow({
  custodianRow,
  facilityIndex = 0,
}: Readonly<AssociateCustodianTableRowProps>) {
  const { isAuthorized } = useAuth();
  const navigate = useNavigate();
  const associatedEntities = custodianRow?.associatedEntities || {};

  const [openCustodianSidebar, setOpenCustodianSidebar] = useState(false);
  const [selectedCustodian, setSelectedCustodian] = React.useState<any>(null);
  const handleOpenCustodianSidebar = (recordType: string, item: any) => {
    setOpenCustodianSidebar(true);
    setSelectedCustodian(item);
  }
  const handleCloseCustodianSidebar = () => setOpenCustodianSidebar(false);

  const [openFacilitySidebar, setOpenFacilitySidebar] = useState(false);
  const handleOpenFacilitySidebar = () => setOpenFacilitySidebar(true);
  const handleCloseFacilitySidebar = () => setOpenFacilitySidebar(false);

  const handleEditFacility = () => {
    navigate(`/associations/edit/${custodianRow.entityId}`);
  };

  const facilityBg = facilityIndex % 2 === 0 ? "#f2f1f1e3" : "#ffffff";

  return (
    <>
      <TableBody
        sx={{
          "& tr": {
            backgroundColor: facilityBg,
          },
          "& tr:last-child td": {
            borderBottom: 0,
          },
        }}
      >
        <TableRow>
          <TableCell
            colSpan={8}
            sx={{
              backgroundColor: facilityBg,
              py: 1,
              px: 2,
              position: "relative",
              borderBottom: "2px solid #e0e0e0",
            }}
          >
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#f76f0eff",
                display: "inline-block",
                cursor: "pointer",
              }}
              onClick={handleOpenFacilitySidebar}
            >
              {custodianRow.facility}
            </Typography>

            {isAuthorized(appActions.editAssociations) && <IconButton
              size="small"
              sx={{ position: "absolute", right: 8, top: 4 }}
              onClick={handleEditFacility}
              disabled={custodianRow.disabled}
            >
              {custodianRow.disabled ?  <EditOffIcon fontSize="small"/> : <EditIcon fontSize="small" />}
            </IconButton>}

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontWeight: "bold", color: "#080300ff", }}
            >
              {`${custodianRow.address.city}, ${custodianRow.address.state} - ${custodianRow.address.postCode}`}
            </Typography>
          </TableCell>
        </TableRow>

        {Object.entries(associatedEntities).map(([recordType, records]) => {
          const entities = records as any[];

          return (
            <React.Fragment key={recordType}>
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#555",
                    background: facilityBg,
                    pl: 3,
                  }}
                >
                  {recordType}
                </TableCell>
              </TableRow>

              {entities.map((item: any, i: number) => (

                <TableRow
                  key={`${recordType}-${i}`}
                  sx={{
                    "&:hover": { backgroundColor: "#fafafa" },
                    backgroundColor: facilityBg,
                  }}
                >
                  <TableCell sx={{ pl: 4, color: "#f5831f", fontWeight: 100 }}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        color: "#f5831f",
                        display: "inline-block",
                        cursor: "pointer",
                      }}
                      onClick={() => { handleOpenCustodianSidebar(recordType, item) }}
                    >
                      {item.name}
                    </Typography>
                  </TableCell>

                  <TableCell>{item.address.state || "-"}</TableCell>
                  <TableCell>{item.address.city || "-"}</TableCell>
                  <TableCell>{item.orderTypeName || "Authorization"}</TableCell>
                  <TableCell>{item.custodianProviderName || "-"}</TableCell>
                  <TableCell>
                    {item.dateFrom
                      ? new Date(item.dateFrom).toLocaleDateString("en-CA")
                      : "NA"}
                  </TableCell>
                  <TableCell>
                    {item.dateTo
                      ? new Date(item.dateTo).toLocaleDateString("en-CA")
                      : "Till Date"}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-block',
                        padding: '2px 12px',
                        borderRadius: '4px',
                        backgroundColor: item.disabled ? red[100] : green[100],
                        color: item.disabled ? red[700] : green[700],
                        fontWeight: '500',
                        fontSize: '12px',
                        textTransform: 'capitalize',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Typography>
                        {item.disabled ? 'Inactive' : 'Active'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          );
        })}
      </TableBody>

      <FacilitySidebar
        open={openFacilitySidebar}
        onClose={handleCloseFacilitySidebar}
        facilityData={custodianRow}
      />

      <CustodianSidebar
        open={openCustodianSidebar}
        onClose={handleCloseCustodianSidebar}
        custodianData={selectedCustodian}        
      />
    </>
  );
}
